import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Loader2, CreditCard, Download, CheckCircle2, Copy, Landmark } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { fmtMoney } from "@/lib/format";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const day = (iso) => (iso ? new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" }) : "");

const usePaidReturn = (token, sessionId, onPaid) => {
  const [checking, setChecking] = useState(!!sessionId);
  useEffect(() => {
    if (!sessionId) return undefined;
    let tries = 0;
    const t = setInterval(async () => {
      tries += 1;
      const r = await api.payStatus(token, sessionId).catch(() => ({ paid: false }));
      if (r.paid) { clearInterval(t); setChecking(false); onPaid(); }
      else if (tries >= 10) { clearInterval(t); setChecking(false); }
    }, 2000);
    return () => clearInterval(t);
  }, [token, sessionId]); // eslint-disable-line react-hooks/exhaustive-deps
  return checking;
};

const Copyable = ({ label, value, testId }) => (
  <div className="flex items-center justify-between gap-3 py-1.5 border-b border-stone-200 last:border-0">
    <span className="text-sm text-stone-500">{label}</span>
    <button type="button" onClick={() => navigator.clipboard?.writeText(value).then(() => toast.success(`${label} copied`))} data-testid={testId}
      className="inline-flex items-center gap-1.5 font-mono text-sm font-semibold text-stone-900 hover:text-stone-600">{value} <Copy className="w-3.5 h-3.5" /></button>
  </div>
);

const Lines = ({ v }) => (
  <div className="mt-6">
    {v.lines.map((l, i) => (
      <div key={i} className="flex justify-between gap-4 py-2 border-b border-stone-200 text-sm" data-testid={`pay-line-${i}`}>
        <span className="text-stone-700">{l.description}{l.qty !== 1 && <span className="text-stone-400"> · {l.qty} × {fmtMoney(l.unit_c)}</span>}</span>
        <span className="tabular-nums font-medium">{fmtMoney(Math.round(l.qty * l.unit_c))}</span>
      </div>
    ))}
    <div className="ml-auto w-56 mt-3 space-y-1 text-sm">
      <div className="flex justify-between text-stone-500"><span>Subtotal</span><span className="tabular-nums">{fmtMoney(v.subtotal_c)}</span></div>
      {v.gst && <div className="flex justify-between text-stone-500"><span>GST (10%)</span><span className="tabular-nums">{fmtMoney(v.gst_c)}</span></div>}
      <div className="flex justify-between font-bold text-base border-t border-stone-300 pt-1.5"><span>Total (AUD)</span><span className="tabular-nums" data-testid="pay-total">{fmtMoney(v.total_c)}</span></div>
    </div>
  </div>
);

const PayBox = ({ v, token }) => {
  const [busy, setBusy] = useState(false);
  const card = async () => {
    setBusy(true);
    try { window.location.href = (await api.payCheckout(token)).url; }
    catch (e) { toast.error(e?.response?.data?.detail || "Card payment is unavailable right now"); setBusy(false); }
  };
  if (v.status === "paid") return <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-5 text-emerald-800 flex items-center gap-3" data-testid="pay-paid"><CheckCircle2 className="w-6 h-6" /><div><p className="font-semibold">Paid, thank you</p><p className="text-sm">Received {day(v.paid_on)}.</p></div></div>;
  if (v.status === "void") return <div className="rounded-2xl bg-stone-100 p-5 text-stone-600" data-testid="pay-void">This invoice has been cancelled. Nothing is owing.</div>;
  return (
    <div className="space-y-4">
      {v.card && <Button onClick={card} disabled={busy} className="w-full h-12 rounded-xl text-base text-white" style={{ background: v.company.color }} data-testid="pay-card-btn">
        {busy ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <CreditCard className="w-5 h-5 mr-2" />} Pay {fmtMoney(v.total_c)} by card</Button>}
      {v.bank && (
        <div className="rounded-2xl border border-stone-200 p-4" data-testid="pay-bank">
          <p className="text-sm font-semibold mb-2 inline-flex items-center gap-2"><Landmark className="w-4 h-4" /> Pay by bank transfer</p>
          {v.bank.name && <Copyable label="Account name" value={v.bank.name} testId="pay-bank-name" />}
          <Copyable label="BSB" value={v.bank.bsb} testId="pay-bank-bsb" />
          <Copyable label="Account no." value={v.bank.account} testId="pay-bank-account" />
          <Copyable label="Reference" value={v.number} testId="pay-bank-ref" />
        </div>
      )}
      {!v.card && !v.bank && <p className="text-sm text-stone-500">Please contact {v.company.name} to arrange payment.</p>}
    </div>
  );
};

export default function PayInvoice() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [v, setV] = useState(null);
  const [err, setErr] = useState("");
  const load = () => api.payView(token).then(setV).catch(() => setErr("This invoice link isn't valid."));
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  const checking = usePaidReturn(token, params.get("session_id"), () => { toast.success("Payment received, thank you"); load(); });
  if (err) return <div className="min-h-screen grid place-items-center bg-stone-50 p-6 text-stone-600" data-testid="pay-error">{err}</div>;
  if (!v) return <div className="min-h-screen grid place-items-center bg-stone-50"><Loader2 className="w-7 h-7 animate-spin text-stone-400" /></div>;
  const tone = { overdue: "text-red-600", paid: "text-emerald-600", void: "text-stone-500" }[v.status] || "text-stone-600";
  const statusText = { overdue: `Overdue · was due ${day(v.due_date)}`, paid: "Paid", void: "Cancelled" }[v.status] || `Due ${day(v.due_date)}`;
  return (
    <div className="min-h-screen bg-stone-100 py-8 px-4 text-stone-900" data-testid="pay-page">
      <div className="max-w-2xl mx-auto bg-white rounded-3xl shadow-sm overflow-hidden">
        <div className="px-6 py-5 flex flex-col items-start sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4" style={{ background: v.company.color }}>
          {v.company.logo_url ? <img src={v.company.logo_url} alt={v.company.name} className="max-h-12 max-w-[200px] bg-white rounded-md p-1" />
            : <span className="text-white font-bold text-xl" data-testid="pay-company">{v.company.name}</span>}
          <span className="text-white/90 font-semibold text-sm tracking-wide whitespace-nowrap" data-testid="pay-title">{v.title} {v.number}</span>
        </div>
        <div className="p-6 sm:p-8">
          <p className={`text-sm font-semibold ${tone}`} data-testid="pay-status">{checking ? "Confirming your payment…" : statusText}</p>
          <p className="font-display text-4xl sm:text-5xl font-bold mt-1 tabular-nums" data-testid="pay-amount">{fmtMoney(v.status === "paid" || v.status === "void" ? 0 : v.total_c)}</p>
          <p className="text-sm text-stone-500 mt-1">{v.status === "paid" || v.status === "void" ? "Nothing owing" : "Amount due"} · Issued {day(v.issue_date)} to {v.client_name}</p>
          {v.job_name && <p className="text-sm text-stone-500 mt-3">Job: {v.job_name}{v.job_address ? ` · ${v.job_address}` : ""}</p>}
          <div className="mt-6"><PayBox v={v} token={token} /></div>
          <Lines v={v} />
          {v.notes && <p className="mt-6 text-sm text-stone-500 whitespace-pre-line">{v.notes}</p>}
          <div className="mt-8 flex flex-wrap items-center justify-between gap-3 text-xs text-stone-400">
            <span>{v.company.name}{v.company.abn ? ` · ABN ${v.company.abn}` : ""}{v.company.address ? ` · ${v.company.address}` : ""}</span>
            <a href={`${BACKEND}/api/pay/${token}/pdf`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-stone-600 hover:text-stone-900 font-medium" data-testid="pay-pdf-link"><Download className="w-4 h-4" /> Download PDF</a>
          </div>
        </div>
      </div>
      <p className="text-center text-xs text-stone-400 mt-4">Invoices by Cross Jobs</p>
    </div>
  );
}
